import type {
  FilterToggle,
  ProductListingPage,
} from "@decocms/apps-commerce/types";

interface HighlightWithHref {
  href?: string;
  text: string;
}

const isOutletPath = (pathname: string) =>
  pathname === "/outlet" || pathname.startsWith("/outlet/");

export function resolveCarouselSizeHref(href: string, pathname: string): string {
  const template = new URL(href, "https://danielatombini.com.br");
  const resolved = new URL(pathname, "https://danielatombini.com.br");

  // Bug real de produção: o filtro de tamanho do outlet trazia produtos de
  // outros departamentos. `resolveCarouselSizeHref` reaproveita o pathname
  // atual (em vez do path do template) pra não perder a subcategoria em que
  // o usuário está navegando dentro de /outlet, mas descartava os params
  // `filter.category-N` do template — a origem do escopo "outlet" na busca.
  // O loader vendor da VTEX Intelligent Search
  // (@decocms/apps/.../productListingPage.ts:344-395) só deriva o facet de
  // categoria a partir do pathname (`pageTypesFromPath`) quando NENHUM
  // `filter.*` está presente na URL — e `filter.tamanho` sempre está aqui.
  // Sem `filter.category-N`, a busca ficava sem nenhum escopo de
  // departamento: `/outlet?filter.tamanho=pp` buscava "tamanho PP" no
  // catálogo inteiro. Mantendo o `filter.category-N` do template garante
  // que o loader sempre recebe o facet de outlet.
  template.searchParams.forEach((value, key) => {
    resolved.searchParams.set(key, value);
  });

  return `${resolved.pathname}${resolved.search}`;
}

export function withDynamicOutletHighlights<T extends HighlightWithHref>(
  highlights: T[] | undefined,
  pathname: string,
): T[] | undefined {
  if (!highlights?.length) {
    return highlights;
  }

  if (!isOutletPath(pathname)) {
    return highlights;
  }

  return highlights.map((highlight) => ({
    ...highlight,
    href: highlight.href
      ? resolveCarouselSizeHref(highlight.href, pathname)
      : highlight.href,
  }));
}

export function buildCarouselSizeFilter(
  highlights: HighlightWithHref[],
  pathname: string,
  search: string,
): FilterToggle {
  const selectedSize = new URLSearchParams(search).get("filter.tamanho")
    ?.toLowerCase();

  const values = highlights
    .filter((highlight): highlight is HighlightWithHref & { href: string } =>
      Boolean(highlight.href)
    )
    .map((highlight) => {
      const href = resolveCarouselSizeHref(highlight.href, pathname);
      const sizeValue = new URL(href, "https://danielatombini.com.br")
        .searchParams.get("filter.tamanho") ?? highlight.text.toLowerCase();

      return {
        value: sizeValue,
        label: highlight.text,
        url: href,
        selected: selectedSize === sizeValue.toLowerCase(),
        quantity: 1,
      };
    });

  return {
    "@type": "FilterToggle",
    key: "tamanho",
    label: "Size",
    quantity: values.length,
    values,
  };
}

export function mergeCarouselSizeFilter(
  filters: ProductListingPage["filters"],
  highlights: HighlightWithHref[] | undefined,
  pathname: string,
  search: string,
): ProductListingPage["filters"] {
  if (!highlights?.length || !isOutletPath(pathname)) {
    return filters;
  }

  const carouselFilter = buildCarouselSizeFilter(highlights, pathname, search);

  if (!carouselFilter.values.length) {
    return filters;
  }

  return [
    ...(filters ?? []).filter((filter) =>
      filter["@type"] !== "FilterToggle" ||
      filter.key.toLowerCase() !== "tamanho"
    ),
    carouselFilter,
  ];
}
